import { useState, useMemo, useRef, useCallback } from "react";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MapPin, Calendar, Info, Globe, ExternalLink, Search, Building, PawPrint } from "lucide-react";
import LoadingFallback from "@/components/LoadingFallback";
import { formatDistanceToNow } from "date-fns";
import { PublicAnimalDetailModal } from "@/components/collector/PublicAnimalDetailModal";
import { useFacilityAnimals, PublicFacilityAnimal } from "@/hooks/use-facility-animals";
import { PublicFacilityAnimalModal } from "@/components/facility/PublicFacilityAnimalModal";

interface PublicProfile {
  username: string;
  display_name: string | null;
  bio: string | null;
  location_city: string | null;
  location_state: string | null;
  show_location: boolean;
  website_url: string | null;
  show_website_url: boolean;
  show_collector_section?: boolean;
  show_collection: boolean;
  show_animal_images: boolean;
  show_animal_morph: boolean;
  show_animal_sex: boolean;
  show_health_status: boolean;
  show_weight_chart?: boolean;
  show_feeding_chart?: boolean;
  show_facility_section?: boolean;
  facility_id_public?: string;
  show_facility_animals_summary?: boolean;
  show_facility_website?: boolean;
  show_facility_location?: boolean;
  show_facility_animals_list?: boolean;
  show_facility_animal_health?: boolean;
  show_facility_animal_feeding?: boolean;
  show_facility_animal_weight?: boolean;
  show_facility_animal_photos?: boolean;
  created_at: string;
}

interface CollectorAnimal {
  id: string;
  species: string;
  common_name: string | null;
  identifier: string | null;
  morph: string | null;
  sex: string | null;
  photo_url: string | null;
  public_health_status: string | null;
  public_detail: {
    health_history: Array<{
      event_type: string;
      relative_time: string;
    }> | null;
    feeding_summary: {
      primary_prey: string;
      typical_interval: string;
      last_fed: string | null;
    };
  } | null;
  public_charts: {
    weight: Array<{
      label: string;
      weight_g: number;
    }> | null;
    feeding: Array<{
      period: string;
      count: number;
    }> | null;
  } | null;
}

const PublicProfile = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<'name' | 'species' | 'health'>('name');
  const [healthFilter, setHealthFilter] = useState<string>('all');
  const [selectedAnimal, setSelectedAnimal] = useState<CollectorAnimal | null>(null);
  const [selectedFacilityAnimal, setSelectedFacilityAnimal] = useState<PublicFacilityAnimal | null>(null);
  const [focusReturnRef, setFocusReturnRef] = useState<HTMLElement | null>(null);
  const tableRowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
  
  // Facility animals state
  const [facilitySearchTerm, setFacilitySearchTerm] = useState("");
  const [facilitySortBy, setFacilitySortBy] = useState<'name' | 'species' | 'health'>('name');
  const [facilityHealthFilter, setFacilityHealthFilter] = useState<string>('all');
  const [facilityStatusFilter, setFacilityStatusFilter] = useState<string>('all');

  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['public-profile', username],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('public_profiles')
        .select('*')
        .eq('username', username)
        .eq('is_public', true)
        .maybeSingle();

      if (error) throw error;
      return data as (PublicProfile & { user_id: string }) | null;
    },
    enabled: !!username,
  });

  const { data: animals = [] } = useQuery({
    queryKey: ['public-profile-animals', username],
    queryFn: async () => {
      if (!username || !profile?.show_collector_section) return [];
      
      const { data, error } = await supabase
        .rpc('get_public_profile_animals', { p_username: username });

      if (error) throw error;
      return data as CollectorAnimal[];
    },
    enabled: !!username && !!profile?.show_collector_section,
  });

  // Fetch facility data if facility section is enabled
  const { data: facilityDataArray } = useQuery({
    queryKey: ['public-facility-data', username],
    queryFn: async () => {
      if (!username || !profile?.show_facility_section) return null;
      
      const { data, error } = await supabase
        .rpc('get_public_facility_data', { p_username: username });

      if (error) throw error;
      return data;
    },
    enabled: !!username && !!profile?.show_facility_section,
  });
  
  const facilityData = facilityDataArray?.[0];
  
  // Fetch facility animals if enabled
  const { data: facilityAnimals = [] } = useFacilityAnimals(
    username,
    !!profile?.show_facility_section && !!profile?.show_facility_animals_list
  );

  // Filter and sort animals - memoized for performance
  const filteredAndSortedAnimals = useMemo(() => {
    let filtered = [...animals];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(animal => 
        (animal.common_name?.toLowerCase().includes(term)) ||
        animal.species.toLowerCase().includes(term)
      );
    }

    // Apply health filter
    if (healthFilter !== 'all') {
      filtered = filtered.filter(animal => 
        animal.public_health_status === healthFilter
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortBy === 'name') {
        const aName = (a.common_name || a.species).toLowerCase();
        const bName = (b.common_name || b.species).toLowerCase();
        return aName.localeCompare(bName);
      } else if (sortBy === 'species') {
        return a.species.toLowerCase().localeCompare(b.species.toLowerCase());
      } else if (sortBy === 'health') {
        const aHealth = a.public_health_status || 'Unknown';
        const bHealth = b.public_health_status || 'Unknown';
        return aHealth.localeCompare(bHealth);
      }
      return 0;
    });

    return filtered;
  }, [animals, searchTerm, healthFilter, sortBy]);

  // Handle modal open with focus management
  const handleOpenAnimal = useCallback((animal: CollectorAnimal, triggerElement: HTMLElement) => {
    setFocusReturnRef(triggerElement);
    setSelectedAnimal(animal);
  }, []);

  // Handle facility animal modal
  const handleOpenFacilityAnimal = useCallback((animal: PublicFacilityAnimal) => {
    setSelectedFacilityAnimal(animal);
  }, []);

  const handleCloseFacilityModal = useCallback(() => {
    setSelectedFacilityAnimal(null);
  }, []);

  // Handle modal close with focus restoration
  const handleCloseModal = useCallback(() => {
    setSelectedAnimal(null);
    // Return focus to the element that triggered the modal
    setTimeout(() => {
      focusReturnRef?.focus();
    }, 0);
  }, [focusReturnRef]);
  
  // Filter and sort facility animals
  const filteredAndSortedFacilityAnimals = useMemo(() => {
    let filtered = [...facilityAnimals];

    // Apply search filter
    if (facilitySearchTerm) {
      const term = facilitySearchTerm.toLowerCase();
      filtered = filtered.filter(animal => 
        (animal.name?.toLowerCase().includes(term)) ||
        (animal.species_common?.toLowerCase().includes(term)) ||
        animal.species_scientific.toLowerCase().includes(term)
      );
    }

    // Apply health filter
    if (facilityHealthFilter !== 'all') {
      filtered = filtered.filter(animal => 
        animal.public_health_status === facilityHealthFilter
      );
    }

    // Apply status filter
    if (facilityStatusFilter !== 'all') {
      filtered = filtered.filter(animal => 
        animal.status === facilityStatusFilter
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (facilitySortBy === 'name') {
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      } else if (facilitySortBy === 'species') {
        return a.species_scientific.toLowerCase().localeCompare(b.species_scientific.toLowerCase());
      } else if (facilitySortBy === 'health') {
        return a.public_health_status.localeCompare(b.public_health_status);
      }
      return 0;
    });

    return filtered;
  }, [facilityAnimals, facilitySearchTerm, facilityHealthFilter, facilityStatusFilter, facilitySortBy]);

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (!profile) {
    return <Navigate to="/404" replace />;
  }

  const joinDate = new Date(profile.created_at).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const location = profile.show_location && profile.location_city && profile.location_state
    ? `${profile.location_city}, ${profile.location_state}`
    : null;

  // Validate website URL
  const websiteUrl = profile.show_website_url && profile.website_url && profile.website_url.startsWith('https://')
    ? profile.website_url
    : null;

  // SEO metadata
  const displayName = profile.display_name || profile.username;
  const pageTitle = `${displayName} – Collection on Zoocertica`;
  
  // Build meta description
  const animalCount = animals.length;
  let metaDescription = `${displayName} shares a public collection of ${animalCount} ${animalCount === 1 ? 'animal' : 'animals'} on Zoocertica.`;
  if (profile.show_collection && animalCount > 0) {
    metaDescription += ' Browse species, morphs, and health summaries.';
  }
  if (location) {
    metaDescription += ` Location: ${location}.`;
  }
  if (metaDescription.length > 160) {
    metaDescription = metaDescription.substring(0, 157) + '...';
  }
  
  // OpenGraph image - use first animal image if available and visible
  const ogImage = profile.show_animal_images && animals.length > 0 && animals[0].photo_url
    ? animals[0].photo_url
    : 'https://zoocertica.com/zoocertica-logo.png'; // fallback to logo
  
  const canonicalUrl = `https://zoocertica.com/u/${username}`;
  
  // JSON-LD structured data
  const structuredData = [];
  
  // Profile schema
  const profileSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": displayName,
    "url": canonicalUrl,
    "description": profile.bio || metaDescription,
    ...(location && profile.show_location ? {
      "address": {
        "@type": "PostalAddress",
        "addressLocality": profile.location_city,
        "addressRegion": profile.location_state
      }
    } : {}),
  };
  structuredData.push(profileSchema);
  
  // Facility schema (if enabled)
  if (profile.show_facility_section && facilityData) {
    const facilitySchema = {
      "@context": "https://schema.org",
      "@type": "Zoo",
      "name": facilityData.facility_name,
      ...(profile.show_facility_location && facilityData.facility_address ? {
        "address": facilityData.facility_address
      } : {}),
      ...(profile.show_facility_animals_summary && facilityData?.animals_count ? {
        "numberOfAnimals": facilityData.animals_count
      } : {}),
    };
    structuredData.push(facilitySchema);
  }
  
  // ItemList for animals (if collection is visible)
  if (profile.show_collector_section && profile.show_collection && animals.length > 0) {
    const animalListSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Animal collection",
      "itemListElement": animals.slice(0, 50).map((animal, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Thing",
          "name": animal.common_name || animal.species,
          "additionalType": "https://schema.org/Animal",
          "description": animal.species + (profile.show_animal_morph && animal.morph ? ` (${animal.morph})` : ''),
          ...(profile.show_animal_images && animal.photo_url ? { "image": animal.photo_url } : {}),
        }
      }))
    };
    structuredData.push(animalListSchema);
  }

  return (
    <>
      <Helmet>
        <html lang="en" />
        {/* Basic meta tags */}
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* OpenGraph tags */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:site_name" content="Zoocertica" />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:alt" content={`Public animal collection for ${displayName}`} />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImage} />
        
        {/* JSON-LD structured data */}
        {structuredData.map((schema, idx) => (
          <script key={idx} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>
      <div className="min-h-screen bg-background">
        {/* Skip to main content link */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg"
        >
          Skip to main content
        </a>
        <header className="border-b bg-card/50 backdrop-blur-sm shadow-sm sticky top-0 z-50" role="banner">
          <div className="container mx-auto px-4 py-4">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md"
              aria-label="Return to Zoocertica homepage"
            >
              <img src="/zoocertica-logo.png" alt="" className="h-8" />
              <span className="text-xl font-semibold bg-gradient-hero bg-clip-text text-transparent">
                ZooCertica
              </span>
            </button>
          </div>
        </header>
        
        <main id="main-content" className="container mx-auto px-4 py-8 sm:py-12 max-w-4xl" role="main">
          {/* Profile Header Card */}
          <Card className="shadow-lg">
            <CardHeader className="text-center space-y-4 pb-6">
              {/* Main title and username */}
              <div className="space-y-2">
                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
                  {profile.display_name || profile.username}
                </h1>
                <p className="text-sm text-muted-foreground">@{profile.username}</p>
              </div>
              
              {/* Metadata badges */}
              <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3">
                <Badge variant="secondary" className="flex items-center gap-1.5 px-3 py-1">
                  <Calendar className="h-3.5 w-3.5" />
                  <span className="text-xs">Joined {joinDate}</span>
                </Badge>
                {location && (
                  <Badge variant="outline" className="flex items-center gap-1.5 px-3 py-1">
                    <MapPin className="h-3.5 w-3.5" />
                    <span className="text-xs">{location}</span>
                  </Badge>
                )}
              </div>

              {/* Website button */}
              {websiteUrl && (
                <div className="pt-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => window.open(websiteUrl, '_blank', 'noopener,noreferrer')}
                    className="gap-2"
                    aria-label={`Visit ${displayName}'s website`}
                  >
                    <Globe className="h-4 w-4" aria-hidden="true" />
                    Visit Website
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </div>
              )}
            </CardHeader>
            
            {/* Bio section */}
            {profile.bio && (
              <CardContent className="pt-0 border-t">
                <div className="prose prose-sm dark:prose-invert max-w-none mt-6">
                  <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">
                    {profile.bio}
                  </p>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Facility Section (shows first if enabled) */}
          {profile.show_facility_section && facilityData && (
            <Card className="mt-8 shadow-lg">
              <CardHeader className="space-y-2">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Building className="h-6 w-6" />
                  Facility Overview
                </CardTitle>
                <CardDescription>{facilityData.facility_name}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {profile.show_facility_location && facilityData.facility_address && (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">{facilityData.facility_address}</p>
                  </div>
                )}
                {profile.show_facility_animals_summary && facilityData?.animals_count && (
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{facilityData.animals_count} animals</Badge>
                  </div>
                )}
                {profile.show_facility_website && facilityData.facility_website && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(facilityData.facility_website, '_blank', 'noopener,noreferrer')}
                    className="gap-2"
                  >
                    <Globe className="h-4 w-4" />
                    Visit Facility Website
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Facility Animals List */}
          {profile.show_facility_section && profile.show_facility_animals_list && facilityAnimals.length > 0 && (
            <Card className="mt-8 shadow-lg">
              <CardHeader className="space-y-2">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Building className="h-6 w-6" />
                  Animals at this Facility ({facilityAnimals.length})
                </CardTitle>
                <CardDescription>Current animal residents</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Filters and sorting controls */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or species..."
                      value={facilitySearchTerm}
                      onChange={(e) => setFacilitySearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Select value={facilitySortBy} onValueChange={(v: any) => setFacilitySortBy(v)}>
                    <SelectTrigger className="w-full sm:w-[180px]">
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name">Sort by Name</SelectItem>
                      <SelectItem value="species">Sort by Species</SelectItem>
                      <SelectItem value="health">Sort by Health</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={facilityHealthFilter} onValueChange={setFacilityHealthFilter}>
                    <SelectTrigger className="w-full sm:w-[180px]">
                      <SelectValue placeholder="Health" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Health</SelectItem>
                      <SelectItem value="Healthy">Healthy</SelectItem>
                      <SelectItem value="Monitoring">Monitoring</SelectItem>
                      <SelectItem value="Special care">Special care</SelectItem>
                      <SelectItem value="Unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={facilityStatusFilter} onValueChange={setFacilityStatusFilter}>
                    <SelectTrigger className="w-full sm:w-[180px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Transferred">Transferred</SelectItem>
                      <SelectItem value="Deceased">Deceased</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Results count */}
                {(facilitySearchTerm || facilityHealthFilter !== 'all' || facilityStatusFilter !== 'all') && (
                  <p className="text-sm text-muted-foreground">
                    Showing {filteredAndSortedFacilityAnimals.length} of {facilityAnimals.length} animals
                  </p>
                )}

                {/* Facility Animals Table */}
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Species</TableHead>
                        {profile.show_facility_animal_health && <TableHead>Health</TableHead>}
                        {profile.show_facility_animal_feeding && <TableHead>Feeding</TableHead>}
                        {profile.show_facility_animal_weight && <TableHead>Weight</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAndSortedFacilityAnimals.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                            No animals match your filters
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredAndSortedFacilityAnimals.map((animal) => (
                          <TableRow 
                            key={animal.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => handleOpenFacilityAnimal(animal)}
                          >
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                {profile.show_facility_animal_photos && animal.photo_url && (
                                  <img 
                                    src={supabase.storage.from('animal-photos').getPublicUrl(animal.photo_url).data.publicUrl} 
                                    alt={animal.name}
                                    className="h-8 w-8 rounded-full object-cover"
                                  />
                                )}
                                <span>{animal.name}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                {animal.species_common && (
                                  <div className="font-medium">{animal.species_common}</div>
                                )}
                                <div className="text-sm text-muted-foreground italic">
                                  {animal.species_scientific}
                                </div>
                              </div>
                            </TableCell>
                            {profile.show_facility_animal_health && (
                              <TableCell>
                                <Badge 
                                  variant="outline"
                                  className={
                                    animal.public_health_status === 'Healthy' ? 'bg-green-500/10 text-green-700 dark:text-green-400' :
                                    animal.public_health_status === 'Monitoring' ? 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400' :
                                    animal.public_health_status === 'Special care' ? 'bg-orange-500/10 text-orange-700 dark:text-orange-400' :
                                    'bg-gray-500/10 text-gray-700 dark:text-gray-400'
                                  }
                                >
                                  {animal.public_health_status}
                                </Badge>
                              </TableCell>
                            )}
                            {profile.show_facility_animal_feeding && (
                              <TableCell className="text-sm text-muted-foreground">
                                {animal.public_feeding_summary}
                              </TableCell>
                            )}
                            {profile.show_facility_animal_weight && (
                              <TableCell className="text-sm text-muted-foreground">
                                {animal.public_weight_summary}
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Collection Section (Collector animals) */}
          {profile.show_collector_section && profile.show_collection && animals.length > 0 && (
            <Card className="mt-8 shadow-lg">
              <CardHeader className="space-y-2">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <PawPrint className="h-6 w-6" />
                  Personal Collection ({animals.length})
                </CardTitle>
                <CardDescription>Active animals in this collection</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Filters and sorting controls */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or species..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
                    <SelectTrigger className="w-full sm:w-[180px]">
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name">Sort by Name</SelectItem>
                      <SelectItem value="species">Sort by Species</SelectItem>
                      <SelectItem value="health">Sort by Health</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={healthFilter} onValueChange={setHealthFilter}>
                    <SelectTrigger className="w-full sm:w-[180px]">
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Animals</SelectItem>
                      <SelectItem value="Healthy">Healthy</SelectItem>
                      <SelectItem value="Under Vet Review">Under Vet Review</SelectItem>
                      <SelectItem value="Recovering">Recovering</SelectItem>
                      <SelectItem value="Unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Results count */}
                {searchTerm || healthFilter !== 'all' ? (
                  <p className="text-sm text-muted-foreground">
                    Showing {filteredAndSortedAnimals.length} of {animals.length} animals
                  </p>
                ) : null}

                {/* Table view with improved styling and semantics */}
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        {profile.show_animal_images && <TableHead scope="col" className="w-24">Image</TableHead>}
                        <TableHead scope="col" className="font-semibold">Name</TableHead>
                        <TableHead scope="col" className="font-semibold">Species</TableHead>
                        {profile.show_animal_sex && <TableHead scope="col" className="font-semibold">Sex</TableHead>}
                        {profile.show_animal_morph && <TableHead scope="col" className="font-semibold">Morph</TableHead>}
                        {profile.show_health_status && <TableHead scope="col" className="font-semibold text-right">Health</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAndSortedAnimals.length === 0 ? (
                        <TableRow>
                          <TableCell 
                            colSpan={[
                              profile.show_animal_images,
                              true, // name
                              true, // species
                              profile.show_animal_sex,
                              profile.show_animal_morph,
                              profile.show_health_status
                            ].filter(Boolean).length} 
                            className="text-center text-muted-foreground py-12"
                          >
                            <div className="flex flex-col items-center gap-2">
                              <Search className="h-8 w-8 opacity-50" aria-hidden="true" />
                              <p>No animals found matching your criteria</p>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredAndSortedAnimals.map((animal) => {
                          const healthStatus = animal.public_health_status;
                          const statusColor = healthStatus === 'Healthy' 
                            ? 'text-green-600 dark:text-green-400'
                            : healthStatus === 'Recovering'
                            ? 'text-yellow-600 dark:text-yellow-400'
                            : healthStatus === 'Under Vet Review'
                            ? 'text-orange-600 dark:text-orange-400'
                            : 'text-muted-foreground';
                          
                          return (
                            <TableRow 
                              key={animal.id}
                              ref={(el) => {
                                if (el) {
                                  tableRowRefs.current.set(animal.id, el);
                                } else {
                                  tableRowRefs.current.delete(animal.id);
                                }
                              }}
                              className="cursor-pointer hover:bg-muted/30 transition-all duration-150 focus-within:bg-muted/30 focus-within:ring-2 focus-within:ring-primary"
                              onClick={(e) => {
                                if (e.currentTarget === e.target || e.currentTarget.contains(e.target as Node)) {
                                  handleOpenAnimal(animal, e.currentTarget);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleOpenAnimal(animal, e.currentTarget);
                                }
                              }}
                              tabIndex={0}
                              role="button"
                              aria-label={`View details for ${animal.common_name || animal.species}`}
                            >
                              {profile.show_animal_images && (
                                <TableCell className="py-3">
                                  {animal.photo_url ? (
                                    <img
                                      src={animal.photo_url}
                                      alt={`${animal.common_name || animal.species}${animal.morph && profile.show_animal_morph ? `, ${animal.morph}` : ''}`}
                                      className="w-20 h-20 object-cover rounded-md shadow-sm"
                                    />
                                  ) : (
                                    <div className="w-20 h-20 bg-muted/70 rounded-md flex items-center justify-center border">
                                      <span className="text-xs text-muted-foreground" aria-label="No photo available">No photo</span>
                                    </div>
                                  )}
                                </TableCell>
                              )}
                              <TableCell className="font-medium py-3">
                                {animal.common_name || animal.species}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground py-3">
                                <em>{animal.species}</em>
                              </TableCell>
                              {profile.show_animal_sex && (
                                <TableCell className="py-3">
                                  {animal.sex ? (
                                    <Badge variant="outline" className="text-xs">
                                      {animal.sex}
                                    </Badge>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">—</span>
                                  )}
                                </TableCell>
                              )}
                              {profile.show_animal_morph && (
                                <TableCell className="py-3">
                                  {animal.morph ? (
                                    <Badge variant="secondary" className="text-xs">
                                      {animal.morph}
                                    </Badge>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">—</span>
                                  )}
                                </TableCell>
                              )}
                                  {profile.show_health_status && (
                                <TableCell className="text-right py-3">
                                  {healthStatus ? (
                                    <TooltipProvider>
                                      <div className="flex items-center justify-end gap-1.5">
                                        <span className={`text-sm font-medium ${statusColor}`}>
                                          {healthStatus}
                                        </span>
                                         <Tooltip>
                                           <TooltipTrigger asChild>
                                             <button 
                                               className="focus:outline-none focus:ring-2 focus:ring-ring rounded"
                                               aria-label={`Health information for ${animal.common_name || animal.species}`}
                                               onClick={(e) => e.stopPropagation()}
                                             >
                                               <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors" aria-hidden="true" />
                                             </button>
                                           </TooltipTrigger>
                                          <TooltipContent className="max-w-xs" side="left">
                                            <p className="text-xs leading-relaxed">
                                              Health status is provided by the collector and is not independently verified by Zoocertica.
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </div>
                                    </TooltipProvider>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">—</span>
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Empty collection state */}
          {profile.show_collection && animals.length === 0 && (
            <Card className="mt-8 shadow-lg">
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">
                  No animals are currently public in this collection.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Footer disclaimer */}
          <div className="mt-12 text-center text-sm text-muted-foreground border-t pt-8 pb-4">
            <p className="font-semibold flex items-center justify-center gap-2">
              <Info className="h-4 w-4" aria-hidden="true" />
              User-generated content — not verified by Zoocertica
            </p>
            <p className="mt-3 text-xs">This is a public collector profile on Zoocertica.</p>
            <p className="mt-1 text-xs">
              <a href="/" className="text-primary hover:underline">Learn more about Zoocertica</a>
            </p>
          </div>
        </main>

        {/* Animal detail modals */}
        {profile && (
          <>
            <PublicAnimalDetailModal
              animal={selectedAnimal}
              isOpen={!!selectedAnimal}
              onClose={handleCloseModal}
              visibilityFlags={{
                show_animal_images: profile.show_animal_images,
                show_animal_morph: profile.show_animal_morph,
                show_animal_sex: profile.show_animal_sex,
                show_health_status: profile.show_health_status,
                show_weight_chart: profile.show_weight_chart ?? false,
                show_feeding_chart: profile.show_feeding_chart ?? false,
              }}
            />
            
            <PublicFacilityAnimalModal
              animal={selectedFacilityAnimal}
              isOpen={!!selectedFacilityAnimal}
              onClose={handleCloseFacilityModal}
              visibilityFlags={{
                show_facility_animal_health: profile.show_facility_animal_health ?? false,
                show_facility_animal_feeding: profile.show_facility_animal_feeding ?? false,
                show_facility_animal_weight: profile.show_facility_animal_weight ?? false,
                show_facility_animal_photos: profile.show_facility_animal_photos ?? false,
              }}
            />
          </>
        )}
      </div>
    </>
  );
};

export default PublicProfile;
